import {
  Chart,
  CommonSeriesSettings,
  Series,
  ValueAxis,
  ArgumentAxis,
  Label,
  Export,
  Format,
  Grid,
  Tooltip,
  Legend,
  Size,
  Title,
} from "devextreme-react/chart";
import { useFetch } from "src/hooks/useFetch";
import { SpatialRun } from "../types";
import { useMemo } from "react";
import { Typography } from "@mui/material";

export enum PlotType {
  CellAssignedCounts,
  CellAssignedRate,
  CellPolonyDropoutRate,
  CellTargetCounts,
}

const CytoCellAssigned = ({
  run,
  type,
}: {
  run: SpatialRun;
  type: PlotType;
}) => {
  const {
    data: rawData,
    isLoading,
    refetch,
    isError,
    error,
  } = useFetch(`/spatialRun/${run?.runID}/cellAssigned`, !!run?.runID);

  const field =
    type === PlotType.CellAssignedCounts
      ? "assignedCountsPerMM2"
      : type === PlotType.CellAssignedRate
      ? "cellularAssignedRate"
      : type === PlotType.CellTargetCounts
      ? "rawAvgTargetCountPerCell"
      : "cellularPolonyDropOutRate";

  const title =
    type === PlotType.CellAssignedCounts
      ? "Cell Assigned Counts"
      : type === PlotType.CellAssignedRate
      ? "Cell Assigned Rate"
      : type === PlotType.CellTargetCounts
      ? "Raw Average Target Count Per Cell"
      : "Cell Polony Dropout Rate";

  const ylabel =
    type === PlotType.CellAssignedCounts
      ? "Counts Per MM2"
      : type === PlotType.CellAssignedRate
      ? "Rate"
      : type === PlotType.CellTargetCounts
      ? "Counts Per Cell"
      : "Rate";

  const data = (rawData as {}[])?.map((v, i) => {
    const obj = {
      Well: run.wells[i].WellLocation,
    };
    for (const key in v) {
      // Convert -999 values back to NaN
      const value = v[key][field];
      obj[key] = value === -999 ? NaN : value;
    }

    return obj;
  });

  console.log("data", data);

  const dataAveraged = useMemo(() => {
    if (!data) return { wellValues: [], runAverage: NaN };
    const wellValues: { value: number; well: string }[] = [];
    const batches = run.batches.filter((b) => b.PMGMask);
    let totalCount = 0;
    let totalSum = 0;
    run.wells.forEach((well, i) => {
      const wellData = data.find((d) => d.Well === well.WellLocation);
      if (wellData) {
        // wellData is now on object where the fields are the batch names and the values are the counts
        // we want to sum the counts
        let sum = 0;
        let count = 0;
        batches.forEach((b) => {
          const value = wellData[b.Name];
          if (value !== undefined && !isNaN(value)) {
            sum += value;
            totalSum += value;
            count++;
            totalCount++;
          }
        });
        let val = NaN;
        if (count > 0) {
          val = sum / count;
        }
        wellValues.push({
          value: type === PlotType.CellAssignedCounts ? sum : val,
          well: well.WellLocation,
        });
      }
    });
    return {
      wellValues,
      runAverage: totalCount > 0 ? totalSum / totalCount : NaN,
    };
  }, [data, run?.batches, run?.wells, type]);

  return (
    <>
      <Typography
        variant="h6"
        style={{ marginTop: "20px", marginBottom: "20px" }}
      >
        Run Average{" "}
        {type === PlotType.CellAssignedCounts
          ? "Cell Assigned Counts"
          : type === PlotType.CellAssignedRate
          ? "Cell Assigned Rate"
          : type === PlotType.CellTargetCounts
          ? "Cell Target Counts"
          : "Cell Polony Dropout Rate"}
        : {dataAveraged.runAverage.toFixed(3)}{" "}
        {type === PlotType.CellAssignedCounts
          ? "Counts Per MM2"
          : type === PlotType.CellAssignedRate
          ? ""
          : ""}
      </Typography>

      <Chart dataSource={data}>
        <Title
          horizontalAlignment="center"
          text={`${run?.runName} ${title} By Well and Batch`}
        />
        <Size width="100%" />
        <CommonSeriesSettings argumentField="Well" type="bar" barPadding={0} />
        {run?.batches
          ?.filter((b) => b.PMGMask)
          .map((b) => (
            <Series key={b.Name} valueField={b.Name} name={b.Name} />
          ))}
        <ValueAxis title={ylabel}>
          <Grid visible={true} />
        </ValueAxis>
        <ArgumentAxis title="Well">
          <Label wordWrap="none" overlappingBehavior="stagger" />
        </ArgumentAxis>
        <Legend
          verticalAlignment="top"
          horizontalAlignment="right"
          // position={"inside"}
        ></Legend>
        <Export enabled />
        <Tooltip enabled>
          <Format type="fixedPoint" precision={3} />
        </Tooltip>
      </Chart>

      <Chart dataSource={dataAveraged.wellValues} style={{ marginTop: "60px" }}>
        <Title
          horizontalAlignment="center"
          text={`${run?.runName} ${
            type === PlotType.CellAssignedCounts ? "" : "Average"
          } ${title} By Well`}
        />
        <Size width="100%" />
        <Series
          argumentField="well"
          valueField="value"
          type="bar"
          barPadding={0}
        />
        <ValueAxis
          title={
            (type === PlotType.CellAssignedCounts ? "Total " : "Average ") +
            ylabel +
            " Across Batches"
          }
        >
          <Grid visible={true} />
        </ValueAxis>
        <ArgumentAxis title="Well">
          <Label wordWrap="none" overlappingBehavior="stagger" />
        </ArgumentAxis>
        <Export enabled />
        <Tooltip enabled>
          <Format type="fixedPoint" precision={3} />
        </Tooltip>
      </Chart>
    </>
  );
};

export default CytoCellAssigned;
